import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { AuthAPI } from '../services/api';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-card-header">
            <div className="auth-logo">⚠️</div>
            <h2>Invalid link</h2>
            <p>This password reset link is missing or invalid.</p>
          </div>
          <div className="auth-card-body" style={{ textAlign: 'center' }}>
            <Link to="/forgot-password" className="btn btn-primary w-100">Request a new link</Link>
          </div>
        </div>
      </div>
    );
  }

  const passwordsMatch = password === confirm;
  const isValid = password.length >= 8 && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setError('');
    setLoading(true);
    try {
      await AuthAPI.resetPassword(token, password);
      navigate('/login?reset=success');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-card-header">
          <img src="/logo.png" alt="KeeDocs" className="auth-logo" />
          <h2>Set new password</h2>
          <p>Choose a strong password for your account</p>
        </div>

        <div className="auth-card-body">
          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="password" className="form-label">New password</label>
              <input
                type="password"
                id="password"
                className="form-control"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="At least 8 characters"
                autoFocus
              />
              {password && password.length < 8 && (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                  Password must be at least 8 characters
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirm" className="form-label">Confirm new password</label>
              <input
                type="password"
                id="confirm"
                className={`form-control ${confirm && !passwordsMatch ? 'is-invalid' : ''}`}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                disabled={loading}
                placeholder="Repeat your password"
              />
              {confirm && !passwordsMatch && (
                <div className="invalid-feedback">Passwords do not match</div>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={loading || !isValid}
            >
              {loading ? 'Resetting…' : 'Reset Password'}
            </button>
          </form>

          <div className="auth-footer">
            <Link to="/login">← Back to Sign In</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
